import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCreatePlatformOrganisation } from '../../hooks/useCreatePlatformOrganisation';

const schema = yup.object({
  organisationName: yup
    .string()
    .required('Organisation name is required')
    .max(100),
  ownerEmail: yup
    .string()
    .required('Email is required')
    .email('Must be a valid email')
    .max(100),
  givenName: yup.string().required('Given name is required').max(100),
  familyName: yup.string().required('Family name is required').max(100),
});

type FormData = yup.InferType<typeof schema>;

interface CreateOrganisationDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CreateOrganisationDialog: React.FC<
  CreateOrganisationDialogProps
> = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      organisationName: '',
      ownerEmail: '',
      givenName: '',
      familyName: '',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const {
    mutate: createOrganisation,
    isPending,
    error,
    reset: resetMutation,
  } = useCreatePlatformOrganisation();

  const handleClose = () => {
    reset();
    resetMutation();
    onClose();
  };

  const onSubmit = (data: FormData) => {
    createOrganisation(data, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      data-testid="create-organisation-dialog"
    >
      <DialogTitle>Create Organisation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, mt: 1 }}
            data-testid="create-organisation-error"
          >
            {error.message}
          </Alert>
        )}

        <Controller
          name="organisationName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Organisation Name"
              fullWidth
              margin="normal"
              error={!!errors.organisationName}
              helperText={errors.organisationName?.message}
              autoFocus
              data-testid="create-organisation-name"
            />
          )}
        />

        <Controller
          name="ownerEmail"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Owner Email"
              type="email"
              fullWidth
              margin="normal"
              error={!!errors.ownerEmail}
              helperText={errors.ownerEmail?.message}
              data-testid="create-organisation-email"
            />
          )}
        />

        <Controller
          name="givenName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Given Name"
              fullWidth
              margin="normal"
              error={!!errors.givenName}
              helperText={errors.givenName?.message}
              data-testid="create-organisation-given-name"
            />
          )}
        />

        <Controller
          name="familyName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Family Name"
              fullWidth
              margin="normal"
              error={!!errors.familyName}
              helperText={errors.familyName?.message}
              data-testid="create-organisation-family-name"
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isPending}
          data-testid="create-organisation-submit"
        >
          {isPending ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
